import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import { useSurveyParams } from '../context/SurveyParamsContext'
import 'survey-core/i18n/traditional-chinese'
import {
  convertVisibleIf,
  convertQuestionMappings,
  convertTriggers,
  q,
  v,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  const { sfdSamplerId, sfdPId } = useSurveyParams()
  // 需要登入嗎
  const requiresLogin = {
    login: false,
    title: '紙上的編號',
    placeholder: 'T000',
    alert: '編號有誤，請重新輸入，或請工作人員協助',
  }
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = null
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  const userIdRef = useRef(null)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)
  // 防止閃閃
  useEffect(() => {
    const timeout = setTimeout(() => {
      const isMobile = window.innerWidth <= 576
      if (!isMobile) return

      const observer = new MutationObserver((mutationsList) => {
        for (let mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const rootEl = document.querySelector('.sd-root-modern')
            if (
              rootEl &&
              !rootEl.classList.contains('sd-root-modern--mobile')
            ) {
              rootEl.classList.add('sd-root-modern--mobile')
            }
          }
        }
      })

      observer.observe(document.getElementById('root'), {
        childList: true,
        subtree: true,
      })

      return () => observer.disconnect()
    }, 100)

    return () => clearTimeout(timeout)
  }, [])

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // 篩出（中止）條件 → 問卷結束
  //  Q1  A1 (3-6歲子女)        = 1(沒有)
  //  Q2  A1-2 (0-未滿3歲子女)  = 1(沒有)
  //  Q3  A2 (是否有工作)       = 4(沒有工作)
  //  Q4  A3 (工作身分)         = 1(雇主) or 2(自營作業者)
  //  Q5  A4 (勞保/就保)        = 2(否)
  const triggersConditionsInit = convertTriggers([
    {
      type: 'complete',
      expression:
        '({q1} = 1) or ({q3} = 4) or ({q4} = 1) or ({q4} = 2) or ({q5} = 2)',
    },
  ])
  const visibleIfConditionsInit = convertVisibleIf({
    // B3 婚姻 = 1/3/4(未婚/離婚/喪偶) → 跳過 B4 配偶工作、C8 配偶育嬰、C9 配偶育嬰月數
    q9: '{q8} = 2',
    q17: '{q8} = 2',
    q18: '{q8} = 2 and {q17} = 1',
    // C4 班別 = 2(輪班) or 3(不固定) → 跳過 C5 工作時間
    q14: '{q13} = 1',
    // C6 沒申請育嬰留職停薪 → 跳過 C7 育嬰月數
    q16: '{q15} = 1',
    // C11 沒換工作 → 跳過 C12 辭職、C13 回職場、C14 經濟來源
    q21: '{q20} = 1',
    // C12 沒辭職 → 跳過 C13、C14
    q22: '{q20} = 1 and {q21} = 1',
    q23: '{q20} = 1 and {q21} = 1',
    // E1 家庭照顧假認知
    //   = 1 有申請過 → 顯示 E2-E5、跳過 E6
    //   = 2 知道沒申請 → 跳過 E2-E5、顯示 E6
    //   = 3 不知道 → 全部跳過
    q28: '{q27} = 1',
    q29: '{q27} = 1',
    q30: '{q27} = 1',
    q31: '{q27} = 1',
    q32: '{q27} = 2',
    // F5 認同/不認同
    //   = 1 or 2 認同 → 顯示 F6、隱藏 F7
    //   = 3 不認同 → 隱藏 F6、顯示 F7
    q38: '{q37} = 1 or {q37} = 2',
    q39: '{q37} = 3',
  })
  const notRequired = null
  const mappedRateChoice = null
  const pageBreakQuestions = null
  const questionMappings = convertQuestionMappings({})

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    requiresLogin,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    setWebAuthType,
    sfdSamplerId,
    sfdPId,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    requiresLogin,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    webAuthType,
    sfdSamplerId,
  })

  useEffect(() => {
    if (agreeSurvey) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [agreeSurvey])

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {}

  const endSurveyMsg = (survey) => {
    const isTerminated =
      survey.getValue(q(1)) === v(1) ||
      survey.getValue(q(3)) === v(4) ||
      survey.getValue(q(4)) === v(1) ||
      survey.getValue(q(4)) === v(2) ||
      survey.getValue(q(5)) === v(2)

    if (isTerminated) {
      setCompletionMessage(
        `<div>非常感謝您協助填答～<br><br>若有任何問題，歡迎聯繫：<br>聯絡人：張先生<br>電話：02-23315133#816<br>手機：0930098480<br>Line ID：zxcvbnm920606</div>`
      )
      return
    }

    setCompletionMessage(
      `<div>非常感謝您協助填答～<br><br>由於後續需要進行約5分鐘的線上回饋訪問，麻煩您從下方網址下載紙本問卷。<br><a href="https://drive.google.com/file/d/1Mqy4J8zykRQZCdJJR2N5l0nw1no_D7E5/view?usp=sharing" target="_blank">https://drive.google.com/file/d/1Mqy4J8zykRQZCdJJR2N5l0nw1no_D7E5/view?usp=sharing</a><br><br>若有任何問題，歡迎聯繫：<br>聯絡人：張先生<br>電話：02-23315133#816<br>手機：0930098480<br>Line ID：zxcvbnm920606</div>`
    )
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    requiresLogin,
    userIdRef,
    enableProgressSaving:
      requiresLogin.login || webAuthType > 0 || !!sfdSamplerId,
    webAuthType,
    sfdSamplerId,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
